// src/pages/HomePage.js

import React from 'react';
import { Link } from 'react-router-dom'; // Import Link for routing
import {
  Container,
  Row,
  Col,
  Button,
  Card
} from 'react-bootstrap';
import { Helmet } from 'react-helmet';
import { Parallax } from 'react-parallax';

function HomePage() {
  return (
    <>
      <Helmet>
        <title>Benign Fasciculation Syndrome (BFS) | Symptoms, Treatment & Support</title>
        <meta
          name="description"
          content="Learn about Benign Fasciculation Syndrome (BFS). Explore symptoms, treatment options, and BFS vs ALS differences. Find support and real recovery stories."
        />
        <link rel="canonical" href="https://benignfasciculationsyndrome.org/" />
      </Helmet>

      {/* Inline Style to Force Montserrat Globally */}
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;600;700&display=swap');

          body, h1, h2, h3, h4, h5, h6, p, .btn, .navbar-brand, .nav-link {
            font-family: 'Montserrat', sans-serif !important;
          }
        `}
      </style>

      {/* ==================== HERO SECTION WITH PARALLAX (React-Parallax) ==================== */}
      <Parallax
        bgImage="https://images.pexels.com/photos/1000445/pexels-photo-1000445.jpeg?auto=compress&cs=tinysrgb&w=1200"
        bgImageAlt="BFS concept"
        strength={300} // Adjust this to control parallax intensity
      >
        {/* The height defines the visible area for the parallax */}
        <div style={{ height: '70vh', position: 'relative' }}>
          {/* Dark overlay to improve contrast */}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              backgroundColor: 'rgba(0,0,0,0.4)',
              zIndex: 1
            }}
          />

          <Container
            fluid
            className="d-flex align-items-center justify-content-center text-center"
            style={{ height: '100%', position: 'relative', zIndex: 2 }}
          >
            <Row>
              <Col>
                <h1 style={{ fontWeight: '700', fontSize: '3rem', color: '#fff' }}>
                  Benign Fasciculation Syndrome (BFS): Symptoms, Treatment & Support
                </h1>
                <p
                  style={{
                    fontSize: '1.25rem',
                    maxWidth: '600px',
                    margin: '1rem auto',
                    color: '#fff'
                  }}
                >
                  Together, we can raise awareness, support research, and empower those affected by BFS.
                </p>
                <div>
                  <Button
                    variant="danger"
                    size="lg"
                    className="me-2 mb-2 mb-sm-0"
                    as={Link}
                    to="/about"
                  >
                    Learn More
                  </Button>

                  <Button
                    variant="outline-light"
                    size="lg"
                    as={Link}
                    to="/contact"
                  >
                    Find Support
                  </Button>
                </div>
              </Col>
            </Row>
          </Container>
        </div>
      </Parallax>

      {/* ==================== "HELP SAVE LIVES" SECTION (Red) ==================== */}
      <div style={{ backgroundColor: '#c8102e', color: '#fff', padding: '4rem 0' }}>
        <Container>
          <Row className="align-items-center">
            <Col md={6} className="text-center text-md-start mb-4 mb-md-0">
              <h2 style={{ fontWeight: '700', fontSize: '2.5rem' }}>
                Learn More About Living with Benign Fasciculation Syndrome
              </h2>
              <p style={{ fontSize: '1.25rem', marginTop: '1rem', maxWidth: '500px' }}>
                Benign Fasciculation Syndrome (BFS) involves frequent, involuntary muscle twitches
                that are typically non-threatening, yet they can still cause concern. Many people find
                that stress management, ample rest, and a well-rounded lifestyle help lessen the frequency
                or intensity of twitching, while professional evaluation can rule out more serious conditions
                and offer reassurance.
              </p>
              <div className="mt-3">
                <Button
                  variant="outline-light"
                  className="fw-semibold rounded-pill"
                  as={Link}
                  to="/donate"
                >
                  Get Involved
                </Button>
              </div>
            </Col>
            <Col md={6}>
              <img
                src="https://images.unsplash.com/photo-1625283518288-00362afc8663?q=80&w=2048&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
                alt="BFS concept"
                style={{
                  width: '100%',
                  borderRadius: '8px',
                  objectFit: 'cover',
                  border: '4px solid #fff'
                }}
              />
            </Col>
          </Row>
        </Container>
      </div>

      {/* ==================== YOUTUBE VIDEO SECTION (SPICED UP) ==================== */}
      <div
        style={{
          background: 'linear-gradient(135deg, #ffffff 0%, #f9fafc 40%, #f3f6fb 100%)',
          padding: '4rem 0',
          borderTop: '4px solid #c8102e'
        }}
      >
        <Container>
          <Row className="gy-4 align-items-center">
            {/* Text / Highlights */}
            <Col md={5}>
              <div
                style={{
                  display: 'inline-block',
                  padding: '0.25rem 0.75rem',
                  borderRadius: '999px',
                  backgroundColor: '#ffe5ea',
                  color: '#c8102e',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  marginBottom: '0.75rem'
                }}
              >
                Doctor Explains BFS
              </div>

              <h2
                style={{
                  fontWeight: '700',
                  fontSize: '2rem',
                  marginBottom: '0.75rem',
                  color: '#222'
                }}
              >
                What Is Benign Fasciculation Syndrome (BFS)?
              </h2>

              <p
                style={{
                  fontSize: '1.05rem',
                  marginBottom: '1rem',
                  color: '#555',
                  lineHeight: 1.6
                }}
              >
                In this short video, a licensed physician walks through what BFS is, how it&apos;s
                diagnosed, and why it&apos;s considered a <strong>benign</strong> condition for most
                people.
              </p>

              <ul style={{ paddingLeft: '1.2rem', marginBottom: '1.25rem', color: '#444' }}>
                <li style={{ marginBottom: '0.4rem' }}>Understand common BFS symptoms and triggers</li>
                <li style={{ marginBottom: '0.4rem' }}>Learn how doctors differentiate BFS from ALS</li>
                <li style={{ marginBottom: '0.4rem' }}>Hear reassuring, evidence-based information</li>
              </ul>

              <Button
                variant="danger"
                className="fw-semibold rounded-pill"
                as={Link}
                to="/understanding-muscle-twitches"
              >
                Learn More About Symptoms
              </Button>
            </Col>

            {/* Video */}
            <Col md={7}>
              <div
                style={{
                  position: 'relative',
                  paddingBottom: '56.25%', // 16:9 ratio
                  height: 0,
                  overflow: 'hidden',
                  borderRadius: '18px',
                  boxShadow: '0 18px 45px rgba(0,0,0,0.18)',
                  backgroundColor: '#000'
                }}
              >
                <iframe
                  src="https://www.youtube.com/embed/xL1CJWlYumM?si=1FSCibkcMp7Hj57V"
                  title="YouTube video player"
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    border: 0
                  }}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                ></iframe>
              </div>
              <p
                style={{
                  fontSize: '0.9rem',
                  color: '#777',
                  marginTop: '0.75rem',
                  textAlign: 'center'
                }}
              >
                Video source: Independent medical education content on BFS. Always discuss your own
                symptoms with your personal physician.
              </p>
            </Col>
          </Row>
        </Container>
      </div>

      {/* ==================== FEATURED ARTICLES (Light Gray) ==================== */}
      <div style={{ backgroundColor: '#f5f5f5', padding: '4rem 0' }}>
        <Container>
          <Row className="mb-4">
            <Col className="text-center">
              <h2 style={{ fontWeight: '700', fontSize: '2rem' }}>Featured Articles</h2>
              <p style={{ fontSize: '1.1rem', maxWidth: '700px', margin: '0.5rem auto' }}>
                Learn about BFS, discover coping strategies, and stay informed on the latest research.
              </p>
            </Col>
          </Row>
          <Row>
            {[
              {
                title: 'Understanding Muscle Twitches',
                text: 'How BFS symptoms can mimic more serious conditions—yet often remain harmless.',
                img: 'https://images.pexels.com/photos/3952231/pexels-photo-3952231.jpeg',
                link: '/understanding-muscle-twitches'
              },
              {
                title: 'Managing Stress & Anxiety',
                text: 'Explore coping strategies, mindfulness techniques, and support options for BFS.',
                img: 'https://images.pexels.com/photos/6408284/pexels-photo-6408284.jpeg',
                link: '/managing-stress'
              },
              {
                title: 'Lifestyle Tips for BFS',
                text: 'Discover how exercise, diet, and routine adjustments can reduce twitch frequency.',
                img: 'https://images.pexels.com/photos/4056723/pexels-photo-4056723.jpeg',
                link: '/lifestyle-tips'
              }
            ].map((article, idx) => (
              <Col md={4} className="mb-4" key={idx}>
                <Card className="h-100 shadow-sm">
                  <div style={{ height: '200px', overflow: 'hidden' }}>
                    <Card.Img
                      variant="top"
                      src={article.img}
                      style={{ objectFit: 'cover', height: '100%', width: '100%' }}
                    />
                  </div>
                  <Card.Body>
                    <Card.Title className="fw-semibold">{article.title}</Card.Title>
                    <Card.Text>{article.text}</Card.Text>

                    <Button variant="danger" as={Link} to={article.link}>
                      Read More
                    </Button>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </div>

      {/* ==================== FIND SUPPORT (Red Section) ==================== */}
      <div
        style={{
          backgroundColor: '#c8102e',
          color: '#fff',
          padding: '4rem 0',
          textAlign: 'center'
        }}
      >
        <Container>
          <Row>
            <Col>
              <h2
                style={{
                  fontWeight: '700',
                  marginBottom: '1rem',
                  fontSize: '2rem'
                }}
              >
                Find Support & Community
              </h2>
              <p
                style={{
                  fontSize: '1.1rem',
                  maxWidth: '700px',
                  margin: '0 auto'
                }}
              >
                Connect with others who understand your journey. Our forums and support groups offer
                guidance and encouragement as you navigate life with BFS.
              </p>

              <div className="d-flex flex-wrap justify-content-center gap-3 my-4">
                <img
                  src="https://randomuser.me/api/portraits/women/47.jpg"
                  alt="Avatar 1"
                  className="rounded-circle"
                  style={{ width: '80px', height: '80px', objectFit: 'cover' }}
                />
                <img
                  src="https://randomuser.me/api/portraits/men/11.jpg"
                  alt="Avatar 2"
                  className="rounded-circle"
                  style={{ width: '80px', height: '80px', objectFit: 'cover' }}
                />
                <img
                  src="https://randomuser.me/api/portraits/women/62.jpg"
                  alt="Avatar 3"
                  className="rounded-circle"
                  style={{ width: '80px', height: '80px', objectFit: 'cover' }}
                />
                <img
                  src="https://randomuser.me/api/portraits/men/95.jpg"
                  alt="Avatar 4"
                  className="rounded-circle"
                  style={{ width: '80px', height: '80px', objectFit: 'cover' }}
                />
                <img
                  src="https://randomuser.me/api/portraits/women/23.jpg"
                  alt="Avatar 5"
                  className="rounded-circle"
                  style={{ width: '80px', height: '80px', objectFit: 'cover' }}
                />
              </div>

              <Button
                variant="light"
                className="fw-semibold rounded-pill mt-2"
                as={Link}
                to="/contact"
              >
                Join the Community
              </Button>
            </Col>
          </Row>
        </Container>
      </div>

      {/* ==================== HOW YOU CAN HELP (Light Gray) ==================== */}
      <div style={{ backgroundColor: '#f5f5f5', padding: '4rem 0' }}>
        <Container>
          <Row className="mb-4 text-center">
            <Col>
              <h2 style={{ fontWeight: '700', fontSize: '2rem' }}>How You Can Help</h2>
              <p
                style={{
                  fontSize: '1.1rem',
                  maxWidth: '700px',
                  margin: '0.5rem auto'
                }}
              >
                Your support fuels research and fosters community outreach.
              </p>
            </Col>
          </Row>
          <Row>
            {[
              {
                title: 'Donate',
                text: 'Your contributions help fund BFS research, awareness efforts, and patient support.',
                img: 'https://cdn-icons-png.flaticon.com/512/3349/3349234.png',
                link: '/donate'
              },
              {
                title: 'Volunteer',
                text: 'Share your time or expertise—assist with forums, plan events, or help with outreach.',
                img: 'https://cdn-icons-png.flaticon.com/128/1006/1006555.png',
                link: '/contact'
              },
              {
                title: 'Partner With Us',
                text: 'Collaborate with our organization to expand BFS awareness on a larger scale.',
                img: 'https://cdn-icons-png.flaticon.com/512/1077/1077012.png',
                link: '/contact'
              }
            ].map((item, idx) => (
              <Col md={4} className="mb-4" key={idx}>
                <Card className="text-center h-100 shadow-sm">
                  <Card.Body>
                    <img
                      src={item.img}
                      alt={`${item.title} icon`}
                      style={{ width: '60px', marginBottom: '1rem' }}
                    />
                    <Card.Title className="fw-semibold">{item.title}</Card.Title>
                    <Card.Text>{item.text}</Card.Text>
                    <Button variant="danger" as={Link} to={item.link}>
                      Learn More
                    </Button>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </div>
    </>
  );
}

export default HomePage;
